import React, {
  Fragment,
  useEffect,
  useMemo,
  useState
} from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
  Typography
} from "@material-ui/core";
import {
  BooleanInput,
  Button,
  FormDataConsumer,
  SelectInput,
  SimpleForm,
  useDataProvider,
  useListContext,
  useNotify
} from "react-admin";
import IconAssess from "@material-ui/icons/Assessment";
import { AssignmentReportTypeEnum, assignmentReportTypeList } from "../const";
import { useField, useFormState } from "react-final-form";
import { Parser } from "json2csv";
import { useSelector } from "react-redux";
import { ReduxState } from "ra-core";
import fileDownload from "js-file-download";
import CustomDateRangeInput from "../../../components/CustomDateRangeInput";
import WaveInput from "./WaveInput";

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  form: {
    "& .MuiCardContent-root": {
      padding: 0,
    },
  },
});

interface IExportButton {
  setIsLoading?: (isLoading: boolean) => void;
}

interface ICloseDialogButton {
  isDisabled: boolean;
  handleCloseDialog: () => void;
}

const ExportButton = ({setIsLoading}: IExportButton) => {
  const { filterValues } = useListContext();
  const { values } = useFormState();
  const { getRecordForCSV } = useDataProvider();
  const notify = useNotify();

  const [isCSVLoading, setCSVLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [progress, setProgress] = useState<{
    current: number | null;
    total: number | null;
  }>({
    current: 0,
    total: 0,
  });

  const downloadReport = () => {
    if (isCSVLoading) return;

    const filter: any = {
      SchoolId: values.school.id,
    };

    if (filterValues.EstimatedStartedAt) {
      filter.EstimatedStartedAt = filterValues.EstimatedStartedAt;
    }

    // Apply Passenger Report specific filters
    if (values.reportType === AssignmentReportTypeEnum.PassengerReport) {
      if (filterValues.BusSchedulePlanId) {
        filter.BusSchedulePlanId = filterValues.BusSchedulePlanId;
      }

      filter.MultipleStatus = ["Ended", "ForceEnded", "EndedByAdmin"];
    }

    // Apply AM Bus Arrival Report specific filters
    if (values.reportType === AssignmentReportTypeEnum.AmBusArrivalReport) {
      if (values.busSchedulePlanIds &&
        values.busSchedulePlanIds.length > 0 &&
        !values.isDownloadAllAmWaves
      ) {
        filter.BusSchedulePlanIds = values.busSchedulePlanIds;
      }

      if (values.startDate) {
        filter.EstimatedStartedAt = values.startDate;
      }

      if (values.endDate) {
        filter.EstimatedEndedAt = values.endDate;
      }

      filter.RouteType = "ToSchool"
    }

    // Set resource based on report type
    let resource = "";
    switch (values.reportType) {
      case AssignmentReportTypeEnum.AreaReport:
      case AssignmentReportTypeEnum.AmBusArrivalReport:
        resource = "assignments";
        break;
      case AssignmentReportTypeEnum.PassengerReport:
        resource = "assignments/passengerReport";
        break;
    }

    // Validate resource
    if (!resource || resource.trim() === "") {
      notify("Invalid report type.", "warning");
      return;
    }

    getRecordForCSV(resource, {
      filter,
      body: values,
      callback: ({ isLoading, loadedRecordCount, totalRecords }: any) => {
        // Only update loading state once at start and end
        if (isLoading !== undefined) {
          setCSVLoading(isLoading);
          setIsLoading && setIsLoading(isLoading);
        }

        setProgress({
          current: loadedRecordCount,
          total: totalRecords,
        });
      },
    })
      .then(({ data }: any) => {
        const parser = new Parser();

        if (!data || data.length === 0) {
          let emptyMessage = `No assignments found for ${values.school.name}`;
          if (values.reportType === AssignmentReportTypeEnum.PassengerReport) {
            emptyMessage = `There are no ended assignment found for ${values.school.name}`;
          }
          notify(emptyMessage, "warning");
          return;
        }

        const csvData = data.flat();

        let fileName = `Assignment Records - ${values.school.name}.csv`;
        switch (values.reportType) {
          case AssignmentReportTypeEnum.AreaReport:
            fileName = `Area Report ${values.school.name} ${filterValues.EstimatedStartedAt}.csv`;
            break;
          case AssignmentReportTypeEnum.PassengerReport:
            fileName = `Passenger Report ${values.school.name} ${filterValues.EstimatedStartedAt}.csv`;
            break;
          case AssignmentReportTypeEnum.AmBusArrivalReport:
            fileName = `AM Bus Arrival Report ${values.school.name} from ${values.startDate} to ${values.endDate}.csv`;
            break;
        }

        fileDownload(parser.parse(csvData), fileName);
      })
      .catch((e: any) => {
        notify(`${e.message}`, "warning");
      })
      .finally(() => {
        // Reset loading state and progress regardless of success or failure
        setCSVLoading(false);
        setIsLoading && setIsLoading(false);
        setProgress({
          current: null,
          total: null,
        });
      });
  };

  const labelButton = useMemo(() => {
    if (!isCSVLoading) {
      return "Download";
    }

    if (progress.current === null && progress.total === null) {
      return "Fetching Assignments";
    }

    if (progress.current !== null && progress.total !== null) {
      let label = `Fetching Data ${progress.current}/${progress.total}`;

      switch (values.reportType) {
        case AssignmentReportTypeEnum.AreaReport:
          label = `Fetching Area Data ${progress.current}/${progress.total}`;
          break;
        case AssignmentReportTypeEnum.PassengerReport:
          label = `Fetching Passenger Data ${progress.current}/${progress.total}`;
          break;
        case AssignmentReportTypeEnum.AmBusArrivalReport:
          label = `Fetching Assignment Data ${progress.current}/${progress.total}`;
          break;
      }

      return label;
    }

    return "Downloading Report";

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCSVLoading, progress]);

  useEffect(() => {
    if (!values.reportType) {
      setIsDisabled(true);
      return;
    }

    if (
      values.reportType === AssignmentReportTypeEnum.AmBusArrivalReport &&
      (!values.startDate || !values.endDate)
    ) {
      setIsDisabled(true);
      return;
    }

    if (
      values.isDownloadAllAmWaves === false &&
      (!values.busSchedulePlanIds || values.busSchedulePlanIds.length === 0)
    ) {
      setIsDisabled(true);
      return;
    }

    setIsDisabled(false);
  }, [values]);

  return (
    <Button
      onClick={downloadReport}
      label={labelButton}
      color="primary"
      disabled={isDisabled}
      autoFocus
    />
  );
}

const AssignmentReportTypeInput = ({ isDisabled }: any) => {
  const isDownloadAllAmWavesField = useField("isDownloadAllAmWaves");
  const busSchedulePlanIdsField = useField("busSchedulePlanIds");

  return (
    <SelectInput
      source="reportType"
      label="Assignment Report Type"
      choices={assignmentReportTypeList}
      disabled={isDisabled}
      onChange={() => {
        isDownloadAllAmWavesField && isDownloadAllAmWavesField.input.onChange(true);
        busSchedulePlanIdsField && busSchedulePlanIdsField.input.onChange([]);
      }}
    />
  );
};

const CloseDialogButton = ({ isDisabled, handleCloseDialog }: ICloseDialogButton) => {
  const isDownloadAllAmWavesField = useField("isDownloadAllAmWaves");
  const busSchedulePlanIdsField = useField("busSchedulePlanIds");

  const handleClickButton = () => {
    isDownloadAllAmWavesField && isDownloadAllAmWavesField.input.onChange(true);
    busSchedulePlanIdsField && busSchedulePlanIdsField.input.onChange([]);
    handleCloseDialog();
  };

  return (
    <Button
      onClick={handleClickButton}
      color="secondary"
      label="Cancel"
      disabled={isDisabled}
      style={{ backgroundColor: "#9e9e9e" }}
    />
  )
}

const AssignmentReportButton = () => {
  const navMode = useSelector((state: ReduxState) => state.navMode);

  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const classes = useStyles();

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Fragment>
      <Button onClick={handleOpenDialog} label="Download Report">
        <IconAssess />
      </Button>

      <SimpleForm
        initialValues={{ school: navMode.school }}
        toolbar={null}
        className={classes.form}
      >
        <Dialog
          open={open}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className={classes.dialog}
          maxWidth="md"
        >
          <DialogTitle id="alert-dialog-title">
            Download Assignment Report {navMode.school?.name}
          </DialogTitle>

          <DialogContent>
            <DialogContentText color="initial">
              <Typography variant="body1" gutterBottom>
                Please select the type of assignment report from the dropdown and
                click the Download button to proceed.
              </Typography>
            </DialogContentText>

            <AssignmentReportTypeInput isDisabled={isLoading} />

            <FormDataConsumer>
              {({ formData }) =>
                (formData.reportType === AssignmentReportTypeEnum.AmBusArrivalReport) &&
                (
                  <>
                    <CustomDateRangeInput
                      fromSource="startDate"
                      toSource="endDate"
                      fromLabel="Start Date"
                      toLabel="End Date"
                      isDisabled={isLoading}
                      gridItemWidth={6}
                    />

                    <BooleanInput
                      style={{marginTop: "10px", marginBottom: "-25px"}}
                      source="isDownloadAllAmWaves"
                      label="Download All AM Waves within Date Range"
                      defaultValue={true}
                      disabled={isLoading}
                    />

                    {!formData.isDownloadAllAmWaves && (
                      <WaveInput
                        source="busSchedulePlanIds"
                        label="Waves"
                        schoolId={navMode.school?.id}
                        scheduleTypeFilter="to_school"
                        isDisabled={isLoading}
                        multipleSelection
                      />
                    )}
                  </>
                )
              }
            </FormDataConsumer>
          </DialogContent>
          <DialogActions>
            <CloseDialogButton
              handleCloseDialog={handleClose}
              isDisabled={isLoading}
            />
            <ExportButton
              setIsLoading={(value) => {
                setIsLoading(value);
              }}
            />
          </DialogActions>
        </Dialog>
      </SimpleForm>
    </Fragment>
  );
}

export default AssignmentReportButton;